import { css } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import CrossIcon from "@NA/Icons/CrossIcon";
import getColor from "@insite/mobius/utilities/getColor";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledButton } from "@insite/mobius/Clickable";

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    partNumbersGridItem?: GridItemProps;
    productPartsDetails?: GridItemProps;
    productPartsDetailsWrapper?: GridItemProps;
    leftColumnGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    imageGridItem?: GridItemProps;
    wrapper?: GridItemProps;
    pdpWrapper?: GridItemProps;
    productLeftGrid?: GridItemProps;
    productRightGrid?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    productStore?: GridItemProps;
    productCompareCheckbox?: InjectableCss;
    productDelivery?: GridItemProps;
    free?: TypographyProps;
    notFree?: TypographyProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    availibilityGrid?: InjectableCss;
    stockMsg?: TypographyProps;
    lowStockIndicator?: TypographyProps;
    divider?: TypographyProps;
    availibility?: TypographyProps;
    pdpAvailibility?: TypographyProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    stockIndicator?: TypographyProps;
    stockAvailability?: GridItemProps;
    pdpSockAvailability?: GridItemProps;
    addToCartWrapper?: InjectableCss;
    quantityOrdered?: TextFieldProps;
    signInDealerPrice?: InjectableCss;
    dealerPriceText?: TypographyProps;
}

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    titleTextCenter?: TypographyPresentationProps;
    pdpTitleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: GridContainerProps;
    carouselProductStyles?: ProductCarouselProductStyles;
    dotsContainerWrapper?: InjectableCss;
}

export const productCarouselStyles: ProductCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        size: 24,
        weight: "bold",

        css: css`
            text-align: left;
            margin: 20px auto 0px;
            line-height: normal;
            color: #002856;
            max-width: 1440px;
            padding-bottom: 30px !important;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 0 0 0px 0!important;
                            font-size: 15px;
                            line-height: 1.35;
                        `,
                        css`
                            margin: 0 0 0px 0!important;
                            font-size: 26px;
                            line-height: 1.35;
                        `,
                        css`
                            margin: 0 0 25px 0;
                            font-size: 26px;
                            line-height: 1.35;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    titleTextCenter: {
        variant: "h2",
        size: 32,
        weight: "bold",

        css: css`
            text-align: center;
            margin: 20px auto;
            margin-bottom: 15px;
            line-height: normal;
            color: #002856;
            max-width: 1440px;
            padding-bottom: 30px !important;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 0 0 0px 0!important;
                            font-size: 15px;
                            line-height: 1.35;
                        `,
                        css`
                            margin: 0 0 0px 0!important;
                            font-size: 26px;
                            line-height: 1.35;
                        `,
                        css`
                            margin: 0 0 15px 0;
                            font-size: 26px;
                            line-height: 1.35;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    pdpTitleText: {
        variant: "h2",
        size: 24,
        weight: "bold",

        css: css`
            text-align: left;
            margin: 0 auto;
            margin-bottom: 30px;
            line-height: normal;
            color: #002856;
            max-width: 1440px;            

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 0 0 20px 0;
                            font-size: 15px;
                            line-height: 1.35;
                        `,
                        css`
                            margin: 0 0 15px 0;
                            font-size: 26px;
                            line-height: 1.35;
                        `,
                        css`
                            margin: 0 0 25px 0;
                            font-size: 26px;
                            line-height: 1.35;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },

    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            justify-content: flex-start;
            position: absolute;
            width: 33px;
            height: 33px;
            left: 10px;
            top: 33%;
            right: auto;
            transform: translate(0, -33%);
            z-index: 10;            
            flex-basis: 5%;
            max-width: 5%;
            outline: none;
            box-shadow: none;
            border: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 35%;
                        transform: translate(0, -35%);
                    `,
                    css`
                        top: 35%;
                        transform: translate(0, -35%);
                    `,
                    css`
                        top: 34%;
                        transform: translate(0, -34%);
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            outline: none;
            right: 0px;
            left: 10px;
            top: 0;
            > ${IconWrapper} {
                width: 33px;
                height: 33px;
                top: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            width: 33px;
                            width: 33px;
                        `,
                    null,
                    null,
                ])}
                svg {
                    width: 33px;
                    height: 33px;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            width: 33px;
                            width: 33px;
                        `,
                    null,
                    null,
                ])}
                }
            }
            
            &:focus {
                outline: none;
            }

            &.disabled {
                cursor: default;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` left: 10px; `,],);
            }}
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            justify-content: flex-end;
            position: absolute;
            width: 33px;
            height: 33px;
            left: auto;
            top: 33%;
            right: 10px;
            transform: translate(0, -33%);
            z-index: 10;
            flex-basis: 5%;
            max-width: 5%;
            outline: none;
            box-shadow: none;
            border: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: 33px;
                        top: 35%;
                        transform: translate(0, -35%);
                    `,
                    css`
                        min-width: 33px;
                        top: 35%;
                        transform: translate(0, -35%);
                    `,
                    css`
                        top: 34%;
                        transform: translate(0, -34%);
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            position: absolute;
            outline: none;
            right: 10px;
            top: 0;
            > ${IconWrapper} {
                width: 33px;
                height: 33px;
                top: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            width: 33px;
                            width: 33px;
                        `,
                    null,
                    null,
                ])}
                svg {
                    width: 33px;
                    height: 33px;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            width: 33px;
                            width: 33px;
                        `,
                    null,
                    null,
                ])}
                }
            }
            &:focus {
                outline: none;
            }
            &.disabled {
                cursor: default;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` right: 10px; `,],);
            }}
        `,
    },
    dotsContainerWrapper: {
        css: css` 
            -webkit-tap-highlight-color:transparent;
            -moz-tap-highlight-color:transparent;
            -o-tap-highlight-color:transparent;
            tap-highlight-color:transparent; 
            li {
                list-style: none;
                padding: 0px;
                cursor:pointer;
                display:flex;
                margin:0px 7.5px;
                height:7px;
                button {
                    width: 55px;
                    height: 7px;
                    padding: 0;
                    border-radius: 5px;
                    margin:0px;
                    border:none;
                    cursor:pointer;
                    background:${ADIColors.gray16};
                    &.active {
                        border-radius: 5px;
                        background:${ADIColors.darkBlue1};                    
                    }
                }                
            }
            display: flex;
            justify-content: center;            
            position: relative;
            width: 100%; 
            max-width: 1440px;
            cursor:pointer;
            margin-bottom:30px;
            display: none;          

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 25px 33.5px 40px 31.5px;
                        justify-content: center;
                        flex-direction: row;
                        max-width: calc(100% - 64px);
                        overflow:hidden; 
                        border-radius: 5px;                       
                        li {
                            margin:0px;
                            flex: 1;
                            button {
                                width:100%;
                                border-radius: 0px;  
                            }
                        }
                    `,
                    css`
                        margin: 25px auto 40px auto;
                        justify-content: center;
                        flex-direction: row;
                        max-width: calc(100% - 92px);
                        overflow:hidden; 
                        border-radius: 5px;                       
                        li {
                            margin:0px;
                            flex: 1;
                            button {
                                width:100%;
                                border-radius: 0px;  
                            }
                        }
                    `,
                    css`
                        margin: 25px auto 35px auto;
                        justify-content: center;
                        flex-direction: row;
                        max-width: calc(100% - 62px);
                        overflow:hidden;                       
                        
                    `,
                    css`
                        margin-top: 25px;
                    `,
                    css`
                        margin-top: 34px;
                    `,
                ])}
        `,
    },
    carouselGridItem: {
        width: 12,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` flex-basis: 100%; max-width: 100%;`,],);
            }}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        gap: 10,
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 10px;
            display: flex;
            flex-direction: column;                        
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 15px; `,], "max",);
            }}
        `,
    },
};

export const productCardStyles: any = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
            border: 1px solid ${getColor("text.disabled")};
            border-radius: 5px;
            position: relative;
            margin: 0 8px 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 30px 0 50px;
                        `,
                        css`
                            padding: 30px 0 50px;
                        `,
                        css`
                            padding: 30px 0 10px;
                        `,
                        css`
                            padding: 30px 0;
                        `,
                        css`
                            padding: 30px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            border: 1px solid ${getColor("common.border")};
            display: flex;
            border-radius: 5px;
            > ${GridOffset} {
                ${({ theme }) => { return breakpointMediaQueries(
                    theme, [null, css` margin: 0 0 0 0; `,],"max",); 
                }}            
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding-top: 25px; `,], "max",);
            }}
        `,
    },
    pdpWrapper: {
        css: css`
       
            position: relative;
            border: 1px solid ${getColor("common.border")};
            display: flex;
            border-radius: 5px;
            background: ${ADIColors.white};
            ${GridOffset} {
                width: 100%;
                margin: 0;
                margin: auto 0 0 0;
                ${({ theme }) => { return breakpointMediaQueries(
                    theme, [null, css` margin: 0 0 0 0; `,],"max",); 
                }}            
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding-top: 25px; `,], "max",);
            }}
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 3, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
            align-items: center;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    tagsWrapper: {
        css: css`
            >div {
                max-width:50%;
                height:24px;
                ${TypographyStyle}{ {
                    height: 24px;
                    min-height: 24px;                    
                    width: auto;
                    padding: 4px 15px!important;
                    white-space: nowrap; 
                    -webkit-line-clamp: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                    display:inline-block;
                    display: flex;
                    line-height: 1;
                    align-items: center;
                    justify-content: center;
                }
            }
        `,
    },
    productLeftGrid: {
        width: [5, 12, 12, 12, 12],
    },
    productRightGrid: {
        width: [7, 12, 12, 12, 12],
        css: css`
            padding: 0px;
            &:first-child {
                padding: 20px !important;
            }
        `,
    },
    imageGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            &:first-child {
                padding: 20px 20px 10px 20px !important;
            }            
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, css` 
                        &:first-child {
                            padding: 25px 0px 10px 0px !important;
                        }
                `,], "max",);
            }}
        `,
    },

    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 5px;
            `,
        },
    },
    productBrandGridItem: {
        width: 12,
        css: css`
            &:first-child {
                padding: 0 20px !important;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px 0px !important; `,], "max",);
            }}
            }
            
        `,
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: 13,
                weight: "600",
                css: css`
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme, [null, css` font-size: 10px;`,], "max",);
                    }}
                `,
            },
            css: css`
                margin-bottom: 5px;
                line-height: normal;
                ${TypographyStyle} {
                    color: ${getColor("text.main")};
                    line-height: normal;
                    :hover {
                        text-decoration: underline;
                        color: ${getColor("text.main")};
                    }
                }
            `,
        },
        nameText: {
            css: css`
                color: ${getColor("text.main")};
                font-size: 13px;
                margin-bottom: 5px;
            `,
        },
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            margin-bottom: 5px;
            padding: 0 20px;
            min-height: initial;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px !important; min-height: initial;`,], "max",);
            }}
            &:first-child {
                padding: 0 20px !important;
            }
            ${TypographyStyle} {
                font-size: 14px;
                line-height: normal;
                color: ${getColor("text.main")};
                font-weight: 600;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                word-break: break-word;
                :hover {
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    pdpDescriptionGridItem: {
        width: 12,
        css: css`
            margin-bottom: 5px;
            padding: 0 20px;
            min-height: initial;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px !important; min-height: initial;`,], "max",);
            }}
            &:first-child {
                padding: 0 20px !important;
            }
            ${TypographyStyle} {
                font-size: 14px;
                line-height: normal;
                color: #2d2926 !important;
                font-weight: 600;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                word-break: break-word;
                :hover {
                    color: ${getColor("text.main")};
                }
            }
        `,
    },

    productPartsDetailsWrapper: {
        width: 12,
        css: css`
            padding: 0 20px 4px 20px;
            min-height: initial;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` margin: 0 auto; padding: 0; `,], "max",);
            }}
        `,
    },
    productPartsDetails: {
        width: 12,
        css: css`
            padding: 0 0px 0px 0px;
            line-height: 1;
            display: block;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [
                    css`
                        text-align: center;
                        justify-content: center;
                        padding: 0;
                    `,
                ],);
            }}
            ${TypographyStyle} {
                margin-right: 5px;
                font-size: 10px;
                color: #666;
                font-weight: 600;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` margin-right: 0px; line-height: normal; `,],);
            }}
            }
        `,
    },

    availabilityByWarehouseModal: {
        sizeVariant: "medium",
        closeButtonIconProps: {
            src: CrossIcon,
            size: 20,
        },
        cssOverrides: {
            modalBody: css`
                border-radius: 4px;
                max-width: 730px !important;
                padding: 28px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                max-width: calc(100% - 20px);
                                height: auto;
                                max-height: 80%;
                                padding: 28px;
                            `,
                            css`
                                max-width: calc(100% - 20px);
                                height: auto;
                                max-height: 80%;
                                padding: 28px;
                            `,
                            css`
                                max-width: 730px;  
                                height: auto;
                                max-height: 80%;
                                padding: 15px;                        
                            `,
                            css`
                                max-width: 730px;                          
                                padding: 28px;
                            `,
                            null,
                        ],
                    )};
                `,
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                border-bottom: 0;
                padding: 0;
                margin-bottom: 20px;
                > h4 {
                    font-size: 32px;
                    font-weight: bold;
                    font-family: "Nunito Sans", sans-serif;
                    line-height: normal;
                    color: ${ADIColors.darkBlue1};
                }
                button {
                    border-radius: 0;
                    svg {
                        height: 20px;
                    }
                }
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    availibilityGrid: {
        css: css`
            order: initial;
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            order: 4;
                            line-height: noraml;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
                > ${TypographyStyle} {
                    margin-right: 10px;
                    button {
                        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` margin-bottom: 0px; `,], "max");
            }}
                    }
                    button:hover{
                        text-decoration: underline;
                        color: ${getColor("text.link")};
                }
        `,
    },
    availibility: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            ${StyledButton} {
                margin-bottom: 0px;
            }
            ${TypographyStyle} {
                font-size: 14px;
                font-weight: bold;
                display: inline;
                margin-top: 0px;
                color: ${getColor("text.link")};
                
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [
                    css`
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            display: block;
                        `,
                ], "max",);
            }}
            }
        `,
    },
    pdpAvailibility: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            ${TypographyStyle} {
                font-size: 14px;
                font-weight: bold;
                display: inline;
                margin-top: 0px;
                color: ${getColor("text.link")};
                
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [
                    css`
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            display: block;
                        `,
                ], "max",);
            }}
            }
        `,
    },
    stockAvailability: {
        width: 12,
        weight: "bold",
        css: css`
            padding: 0 20px 20px;
            padding: 0 20px 0px;
            padding-bottom: 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px 7px; `,], "max",);
            }}
            
            ${TypographyStyle} {
                ${TypographyStyle} {
                    ${StyledButton}{
                        &:hover {
                            text-decoration: underline;
                            color: ${getColor("text.link")};
                            ${TypographyStyle} {
                                color: ${getColor("text.link")};
                            }
                        }
                    }
                } 
            }
        `,
    },
    pdpStockAvailability: {
        width: 12,
        weight: "bold",
        css: css`
            padding: 0 20px 20px;
            padding: 0 20px 0px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px 7px; `,], "max",);
            }}
            
            ${TypographyStyle} {
                ${TypographyStyle} {
                    ${StyledButton}{
                        &:hover {
                            text-decoration: underline;
                            color: ${getColor("text.link")};
                            ${TypographyStyle} {
                                color: ${getColor("text.link")};
                            }
                        }
                    }
                } 
            }
        `,
    },
    stockMsg: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            font-weight: 600;
            color: #000;
        `,
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    ADIproductPrice: {
        width: 12,
        css: css`
        padding: 0 20px 0px 20px;
        line-height:normal;
        min-height: initial;
        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [
                    css`
                    min-height: initial;
                    padding: 0 10px 5px 10px;
                `,
                ], "max",);
            }}
        div:last-child {
            p:first-child{
                margin-top: 2px;
                line-height: 1;
            }

            p:last-child{
                margin-top: 4px;
                line-height: 1;
            }
            &:not(:priceText){
            ${TypographyStyle}{
                margin-top: 2px;
                line-height: 1;
            }  
        } 
            
        }
        `,
    },
    ADIproductPriceWrapper: {},
    ProductPrice: {
        width: 12,
        css: css`
            font-weight: bold;
            padding: 0 20px 15px;
        `,
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
            `,
        },
        priceWrapperCustom: {
            css: css`
                display: flex;
                flex-direction: row;
                gap: 10px;
                width: 100%;
            `,
        },
        priceLabelText: {
            variant: "legend",
        },
        priceWrapper: {
            css: css``,
        },

        price: {
            priceText: {
                size: "30px",
                css: css`
                + ${TypographyStyle} {
                    margin-top:4px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #666;
                }
                ${TypographyStyle}{
                    color: rgb(102, 102, 102) !important;
                    margin-top:23px;
                }
                    flex-direction: row;
                    flex-wrap: nowrap;
                    display: flex;
                    white-space: nowrap;
                    color: ${getColor("text.main")};
                    flex-wrap: wrap;
                    sup:first-child {
                        display: inline-block;
                        margin-top: 5px;
                        font-size: 18px;
                        line-height: normal;
                        font-weight: 900;
                        margin-left: 0;
                        margin-right: 2px;
                        position: relative;
                    }

                    span {
                        font-size: 30px;
                        font-weight: 900;
                        line-height: normal;
                        margin-bottom: 0px;
                        display: block;
                        //position: relative;
                        //top: 2px;
                    }
                    
                    sup.currencyText {
                        font-weight: 900;
                        font-size: 18px;
                        font-stretch: normal;
                        line-height: normal;
                        margin-left: 2px;
                        margin-top: 3px;
                        position: relative;
                    }
                    sup.unitofmeasureText {
                        font-weight: 600;
                        font-size: 12px;
                        line-height: normal;
                        margin-left: 2px;
                        margin-top: 5px;
                        position: relative;
                    }
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                sup:first-child {
                                    font-size: 12px;
                                    margin-top: 4px;
                                }
                                span {
                                    font-size: 20px;
                                }
                                sup.currencyText,
                                sup.unitofmeasureText {
                                    font-size: 12px;
                                    margin-top: 2.5px;

                                }
                                `,
                            ],
                            "max",
                        );
                    }}
                `,
            },
            errorText: { weight: "bold" },
            realTimeText: {
                weight: "bold",
                css: css`
                    display: inline-block;
                    text-align: left;
                    min-width: 25px;

                    &::after {
                        overflow: hidden;
                        display: inline-block;
                        vertical-align: bottom;
                        animation: ellipsis steps(4, end) 900ms infinite 1s;
                        content: \\2026;
                        width: 0;
                    }

                    @keyframes ellipsis {
                        to {
                            width: 1.25em;
                        }
                    }
                `,
            },
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
            vatLabelText: {
                css: css`
                    font-size: 12px;
                `,
            },
        },
        invalidPriceText: {
            color: "danger",
        },
        secondaryPriceWrapper: {
            css: css`
                margin-top: 5px;
            `,
        },
        secondaryPrice: {
            priceText: {
                weight: "bold",
                css: css`
                    color: ${ADIColors.darkBlue4};
                    font-size: 18px;
                    font-weight: 900;
                `,
            },
            errorText: { weight: "bold" },
            realTimeText: {
                weight: "bold",
                css: css`
                    display: inline-block;
                    text-align: left;
                    min-width: 25px;

                    &::after {
                        overflow: hidden;
                        display: inline-block;
                        vertical-align: bottom;
                        animation: ellipsis steps(4, end) 900ms infinite 1s;
                        content: \\2026;
                        width: 0;
                    }

                    @keyframes ellipsis {
                        to {
                            width: 1.25em;
                        }
                    }
                `,
            },
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                    top: -5px;
                    position: relative;
                    font-weight: 600;
                    color: rgb(102, 102, 102) !important;
                    flex: 1 1 100%;
                    width: 6px;
                    font-size: 12px !important;
                    display: block !important;
                    margin-top: 0px !impoortant; 
                `,
            },
            vatLabelText: {
                css: css`
                    font-size: 12px;
                `,
            },
        },
        packWrapper: {
            css: css`
                margin-bottom: 8px;
            `,
        },
        packLabelText: {
            weight: "bold",
        },
        savingsMessageText: {
            italic: true,
            size: "12px",
        },
        quoteMessage: {
            text: { weight: "bold" },
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    productStore: {
        width: 12,
        weight: "bold",
        css: css`
            padding: 0 20px 15px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px 15px; `,], "max",);
            }}
            svg {
                margin-right: 5px;
            }
            ${TypographyStyle} {
                font-size: 12px;
                font-weight: bold;
                color: #002856;
                display: block;
                line-height: normal;
            }
        `,
    },

    productDelivery: {
        width: 12,
        weight: "bold",
        css: css`
            padding: 0 20px 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [css` padding: 0 10px 10px; `,], "max",);
            }}
            svg {
                margin-right: 4px;
            }
            ${TypographyStyle} {
                font-size: 12px;
                font-weight: bold;
                display: block;
                line-height: 16px;
                &:first-child {
                    color: #002856;
                }
            }
        `,
    },
    free: {
        css: css`
            color: #0daa00;
        `,
    },
    notFree: {
        css: css`
            color: #f88411;
        `,
    },
    addToCartMainContainer: {
        width: 12,
        css: css`
            padding: 0;
            height: 63px;
            margin: auto 0 0 0!important;
        `,
    },

    productCompareCheckbox: {
        css: css`
            width: 100%;
            position: absolute;
            [data-test-selector="ProductComapreCheckbox"] {
                padding-top: 0px !important;
                > div > [data-id="checkbox"] {
                    cursor: pointer;
                    flex-direction: row-reverse;
                    margin: 4px 10px 4px 0px;
                    position: relative;
                    float: right;
                    > label {
                        //margin-right: 0px;
                    }
                }                    
            }
        `
    },

    addToCartContainer: {
        css: css`
            flex-grow: 0;
            padding-top: 20px;
            flex-basis: 100%;
            background: #fff;
            border-radius: 0px 0px 5px 5px;
            @media screen and (max-width: 767px) {
                //margin-left: -66%;
                padding-top: 10px;
                > div {
                    height: 100%;
                    display: flex;
                    button {
                        height: 64px;
                    }
                }
            }
        `,
    },
    addToCartButtonGridItem: {
        css: css`
            button {
                height: 64px;
                border: 0px !important;
                border-top: 1px solid ${ADIColors.gray7} !important;
                margin-bottom: -1px;
                margin-bottom: 0px;
                border-radius: 0 0 5px 5px;
                &:hover {
                    border-radius: 0 0 5px 5px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            padding: 0;
                            height: 40px;
                            border-top: 0px;
                        `,
                    css`
                            padding: 0;
                            height: 40px;
                            border-top: 0px;
                        `,
                    null,
                    null,
                    null,
                ])}
            }
        `,
    },

};
